"use client";

import { useEffect } from "react";

const SCROLL_HANDLED_URL_KEY = "__gamjaHashScrollHandledUrl";

/** 호출자가 직접 스크롤한 위치를 유지하면서 해시와 대상 강조만 갱신한다. */
export function replaceHashWithoutScroll(hash: string): void {
  const url = new URL(window.location.href);
  url.hash = hash;
  // Next.js가 나중에 같은 URL을 history에 반영할 때도 스크롤 소유 정보를 유지한다.
  // Next 내부 상태는 넘기지 않아 라우터의 URL 갱신을 건너뛰지 않도록 한다.
  window.history.replaceState({ [SCROLL_HANDLED_URL_KEY]: url.href }, "", url);
}

export interface HashHighlighterProps {
  /**
   * 스크롤 대상을 찾기 전에 실행할 앱별 전처리.
   * 레이아웃을 크게 바꿨으면 true 를 반환한다(리플로우·페인트를 기다린 뒤 스크롤한다).
   * 예: JCG 의 해시 언어 접미사(-c/-java/-python) 감지 후 코드 언어 전환.
   *
   * 주의: 마운트 시 이 컴포넌트(자식)의 effect가 래퍼(부모)의 effect보다 먼저 실행되므로,
   * 여기서 dispatch한 이벤트는 래퍼 effect가 등록하는 리스너에 아직 닿지 않는다.
   * 코어가 true 반환 직후 onResolveTick 을 동기 실행해 DOM 재패치는 보장하지만,
   * 그 밖의 리스너 기반 부수효과를 이 시점에 기대하지 말 것.
   */
  onBeforeResolve?: (hash: string) => boolean;
  /**
   * 대상 탐색을 재시도할 때마다 실행할 앱별 DOM 패치.
   * 예: JCG 의 헤딩 id 언어 접미사 패치.
   */
  onResolveTick?: () => void;
}

/**
 * 해시로 진입한 페이지를 대상 요소가 실제로 렌더·가시화된 뒤에 스크롤한다.
 *
 * 브라우저의 기본 해시 스크롤은 문서가 짧은 초기 페인트 시점에 일어나므로,
 * 하이드레이션으로 콘텐츠가 자라는 페이지에서는 엉뚱한 위치에 착지한다.
 * 여기서는 대상이 나타날 때까지 100ms 간격으로 최대 3초 재시도한 뒤 스크롤한다.
 *
 * 대상은 헤딩(h1~h6) 또는 `problem-` 으로 시작하는 id 다.
 * 문제 앵커는 화면 중앙에 놓고 5초간 `.highlighted` 를 부여한다.
 */
export default function HashHighlighter({
  onBeforeResolve,
  onResolveTick,
}: HashHighlighterProps = {}) {
  useEffect(() => {
    onResolveTick?.();

    const isHashTarget = (element: HTMLElement) =>
      /^h[1-6]$/i.test(element.tagName) || element.id.startsWith("problem-");

    const scrollToTarget = (element: HTMLElement, shouldScroll: boolean) => {
      if (shouldScroll) {
        const block = element.id.startsWith("problem-") ? "center" : "start";
        element.scrollIntoView({ behavior: "smooth", block });
      }
      element.classList.add("highlighted");
      setTimeout(() => {
        element.classList.remove("highlighted");
      }, 5000);
    };

    const highlightElement = (hash: string, shouldScroll = true) => {
      document.querySelectorAll(".highlighted").forEach((el) => {
        el.classList.remove("highlighted");
      });

      if (!hash) return;

      const layoutChanged = onBeforeResolve?.(hash) ?? false;

      let retries = 0;
      const maxRetries = 30; // 최대 3초
      const scrollToHash = () => {
        onResolveTick?.();

        const elementId = hash.slice(1);
        const decodedId = decodeURIComponent(elementId);

        let element = document.getElementById(elementId) as HTMLElement | null;
        if (!element) {
          element = document.getElementById(decodedId) as HTMLElement | null;
        }

        // offsetParent 가 null 이면 아직 화면에 없다(display:none 또는 미렌더).
        if (element && isHashTarget(element) && element.offsetParent !== null) {
          scrollToTarget(element, shouldScroll);
        } else if (retries < maxRetries) {
          retries++;
          setTimeout(scrollToHash, 100);
        }
      };

      if (layoutChanged) {
        // 레이아웃이 크게 바뀐 경우 리플로우+페인트 완료 후 스크롤
        requestAnimationFrame(() => {
          requestAnimationFrame(scrollToHash);
        });
      } else {
        scrollToHash();
      }
    };

    const handleHashChange = () => {
      setTimeout(() => highlightElement(window.location.hash), 50);
    };

    const handlePopState = () => {
      setTimeout(() => highlightElement(window.location.hash), 50);
    };

    // 전처리는 마운트 시점에 동기로 끝낸다. 아래 highlightElement 안에서도 불리지만
    // 그것만 믿으면 100ms 뒤로 밀려, 그 사이 잘못된 상태가 먼저 그려질 수 있다
    // (JCG: -python 해시로 들어왔는데 다른 언어가 보이는 구간). 원본도 같은 이유로 여기서 불렀다.
    // 두 번째 호출은 이미 반영된 상태라 false 를 돌려주고 그대로 스크롤로 넘어간다.
    if (window.location.hash) {
      // 전처리가 레이아웃을 바꿨으면 즉시 재패치한다(onBeforeResolve JSDoc 참고 — 래퍼의
      // 이벤트 리스너는 부모 effect라 이 시점엔 등록 전이고, 다음 소비자가 50ms에 DOM을 읽는다).
      if (onBeforeResolve?.(window.location.hash)) {
        onResolveTick?.();
      }
      setTimeout(() => highlightElement(window.location.hash), 100);
    }

    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("popstate", handlePopState);

    // Next.js 라우터의 pushState/replaceState 로 바뀌는 해시도 감지한다.
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    const scheduleHistoryHighlight = () => {
      // 호출자가 이미 이동한 URL에 대한 라우터의 후속 history 반영은 강조만 한다.
      // 일반 history 갱신은 이 표시를 전달하지 않으며, 다른 URL에도 적용되지 않는다.
      const shouldScroll =
        history.state?.[SCROLL_HANDLED_URL_KEY] !== window.location.href;
      setTimeout(() => {
        if (window.location.hash) {
          highlightElement(window.location.hash, shouldScroll);
        }
      }, 50);
    };

    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      scheduleHistoryHighlight();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      scheduleHistoryHighlight();
    };

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handlePopState);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [onBeforeResolve, onResolveTick]);

  return null;
}
